import {
  AD_TYPES,
  SINGLE_PLAN_STEPS,
  SPONSORED_DISPLAY_CREATIVES_STEP,
  WALMART_CREATIVES_STEP,
  BIDDING_RULES_SUBSTEP,
} from "./constants";

const getAdTypeSubsteps = (state, adTypeStep) => {
  switch (state.adType) {
    case AD_TYPES.SPONSORED_BRAND.value:
      if (state.autoCollection) {
        return adTypeStep.autoCollectionSubsteps;
      }
      if (state.storeSpotlight) {
        return adTypeStep.storeSpotlightSubsteps;
      }
      if (state.storeLandingPage) {
        // SB picks the brand on the overview substep (unlike SB Video, which shares these substeps but only renders
        // the brand selector on the landing page substep), so validate it there instead of a substep later
        return adTypeStep.storePageSubsteps.map((substep) =>
          substep.key === "overview" ? { ...substep, validations: [...substep.validations, "brand"] } : substep,
        );
      }
      if (state.newLandingPage || state.newProductCollection || state.manualCollection) {
        return adTypeStep.landingPageSubsteps;
      }
      return adTypeStep.defaultSubsteps;
    case AD_TYPES.SPONSORED_BRAND_VIDEO.value:
      return state.storeLandingPage ? adTypeStep.storePageSubsteps : adTypeStep.sbVideoSubsteps;
    default:
      return adTypeStep.defaultSubsteps;
  }
};

const getProductSubsteps = (state, productStep) => {
  if (state.newLandingPage || state.storeLandingPage || state.newProductCollection || state.manualCollection) {
    return productStep.landingPageSubsteps;
  }
  return state.autoCollection ? productStep.autoCollectionSubsteps : productStep.defaultSubsteps;
};

// The single plan wizard's steps for the plan as it stands: substeps follow the ad type and the options picked so
// far, and some ad types get a creatives step after product selection. Always new arrays, never SINGLE_PLAN_STEPS
// itself, so one plan's steps can't leak into the next.
export const getSingleFormSteps = (channel, state) => {
  const [setupStep, adTypeStep, productStep, targetingStep, reviewStep] = SINGLE_PLAN_STEPS;
  const steps = [
    setupStep,
    { ...adTypeStep, substeps: getAdTypeSubsteps(state, adTypeStep) },
    {
      ...productStep,
      substeps: getProductSubsteps(state, productStep),
      // A store spotlight on an existing store page takes its products from the page
      isHidden: Boolean(
        state.storeSpotlight && !state.newLandingPage && state.adType === AD_TYPES.SPONSORED_BRAND.value,
      ),
    },
    {
      ...targetingStep,
      substeps: [
        ...(state.renderAdvancedOptions ? targetingStep.funnelSubsteps : targetingStep.defaultSubsteps),
        ...(state.createNewProgram ? [BIDDING_RULES_SUBSTEP] : []),
      ],
    },
    reviewStep,
  ];

  if (
    channel === "walmart" &&
    [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType)
  ) {
    steps.splice(WALMART_CREATIVES_STEP.placementIndex, 0, WALMART_CREATIVES_STEP);
  }
  if (channel === "amazon" && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    steps.splice(SPONSORED_DISPLAY_CREATIVES_STEP.placementIndex, 0, SPONSORED_DISPLAY_CREATIVES_STEP);
  }

  return steps;
};

// Handles cases where an extra parent step or substep are added or removed out of normal form flow based on add type
// or extra steps being required to support the new bidding programs form
export const getExemptNavPaths = (state, step, substep) => {
  if (state.adType === AD_TYPES.SPONSORED_BRAND_WALMART.value || state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    return step === 4 && substep === 1;
  }

  if (state.storeSpotlight && state.page?.value) {
    return step === 2 && substep === 1;
  }

  if (state.biddingProgram) {
    return false;
  }

  return step === 3 && substep === 1;
};
